import React, { useEffect, useRef, useState } from "react"
import useAuth from "../../context/userContext"
import { SuccessNotification, ErrorNotification } from "../../utils"
import api, { BASE_URL, fetcher } from "../../utils/api"
import DashboardHeader from "../../components/DashboardHeader"
import { useForm } from "react-hook-form"
import { useLocationCascade } from "../../hooks/useLocations"
import { RiEyeLine } from "react-icons/ri"
import { FaRegEyeSlash } from "react-icons/fa"
import { MdOutlineEmail } from "react-icons/md"
import { BiMessageDots } from "react-icons/bi"
import AccountConfirmModal from "../../components/modals/AccountConfirmModal"
import { GoQuestion } from "react-icons/go"
import { Tooltip } from "../../layout/sidebar"
import useSWR from "swr"
import Spinner from "../../components/Spinner"
import RequestSenderIdForm from "../../components/sms/RequestSenderIdForm"
import RequestPrivateNumberSection from "../../components/sms/RequestPrivateNumberSection"
import {
  getSubmittedSenderId,
  getActiveSenderId,
  getApprovedCustomSenderId,
  getAccountCustomSenderId,
  isPendingCustomRequest,
  isApprovedCustomSenderId,
  PLATFORM_DEFAULT_SENDER_ID,
} from "../../utils/smsSenderId"

const OWNER_ONLY_TABS = ["SMS Nickname", "Private Number"]

const tabs = [
  "My details",
  "Password",
  "Preference",
  "SMS Nickname",
  "Private Number",
]

const Index = () => {
  const { permissions } = useAuth()
  const [activeTab, setActiveTab] = useState("My details")

  const isManagedUser = Boolean(permissions?.isManagedUser)
  const permissionTabs = tabs.filter((tab) => !isManagedUser || !OWNER_ONLY_TABS.includes(tab))

  const activeTabHandler = (tab) => {
    setActiveTab(tab)
    localStorage.setItem("activeTab", tab)
  }

  useEffect(() => {
    let tab = localStorage.getItem("activeTab") || "My details"
    if (tab === "SMS Nicknames" || tab === "SMS Nickname & Private Number") {
      tab = "SMS Nickname"
    }
    if (isManagedUser && OWNER_ONLY_TABS.includes(tab)) {
      tab = "My details"
      localStorage.setItem("activeTab", tab)
    }
    setActiveTab(tab)
  }, [isManagedUser])

  return (
    <div className="w-full lg:px-10 px-2 h-full py-10 space-y-4">
      <DashboardHeader label="Settings & Profile" />

      <div className='mt-5 border-b border-gray-200 dark:border-border-dark w-full'>
        {permissionTabs.map((item) => {
          return <button
            className={`px-4 border-b-2 pb-3 text-base transition-all duration-300 ${activeTab === item ? "text-yellowcolor border-yellowcolor" : "text-gray-700 dark:text-white border-transparent"}`}
            onClick={() => activeTabHandler(item)}>
            {item}
          </button>
        })
        }
      </div>


      <div className="">
        {activeTab === "My details" && <PersonalInformation />}
        {activeTab === "Password" && <ChangePassword />}
        {activeTab === "Preference" && <AccountPreferences />}
        {activeTab === "SMS Nickname" && !isManagedUser && <SenderIdManagement />}
        {activeTab === "Private Number" && !isManagedUser && <PrivateNumberManagement />}
      </div>


    </div>

  )
}

const AccountPreferences = () => {
  const { user } = useAuth()
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    otp: false,
  })
  const [confirmModal, setConfirmModal] = useState(false)

  const updateSetting = async (setting) => {
    try {
      let response

      if (setting === "emailNotifications") {
        response = await fetch(`${BASE_URL}users/${user?.id}/toggle-email-notifications/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token")}`
          },
        })
        const data = await response.json()
        setSettings((prev) => ({
          ...prev,
          emailNotifications: data.receive_email_notifications,
        }))
      } else if (setting === "smsNotifications") {
        response = await fetch(`${BASE_URL}users/${user?.id}/toggle-sms-notifications/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token")}`
          },
        })
        const data = await response.json()
        setSettings((prev) => ({
          ...prev,
          smsNotifications: data.receive_sms_notifications,
        }))
      } else if (setting === "otp") {
        response = await fetch(`${BASE_URL}users/${user?.id}/toggle-two-factor-auth/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token")}`
          },
        })
        const data = await response.json()
        setSettings((prev) => ({
          ...prev,
          otp: data.two_factor_auth,
        }))
      }
      fetchUserSettings()
    } catch (error) {
      ErrorNotification(error?.message || "Request failed. Please try again")
    }
  }

  const handleToggle = (setting) => {
    const newValue = !settings[setting]
    setSettings((prev) => ({
      ...prev,
      [setting]: newValue,
    }))
    updateSetting(setting)
  }

  const fetchUserSettings = async () => {
    try {
      const res = await fetch(`${BASE_URL}users/me/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      const data = await res.json()

      setSettings({
        emailNotifications: data.receive_email_notifications,
        smsNotifications: data.receive_sms_notifications,
        otp: data.two_factor_auth,
      })
    } catch (error) {
      ErrorNotification("Failed to load user settings.")
    }
  }

  useEffect(() => {
    if (!user?.id) return
    fetchUserSettings()
  }, [user?.id])

  return (
    <>
      {confirmModal && <AccountConfirmModal onClose={() => setConfirmModal(false)} onConfirm={() => { }} />}
      <div className="space-y-4">
        <div className="border-b pb-3 border-gray-200  dark:border-border-dark w-full">
          <h1 className="text-base font-bold dark:text-white">
            Account Preference
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            Enable preferred features below
          </p>
        </div>

        <div className="space-y-4">
          {/* Email notifications */}
          <div className="border dark:border-border-dark border-lightGray2 rounded-lg p-6 flex items-center justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-yellow-100/50 w-8 h-8 rounded-full flex items-center justify-center">
                <MdOutlineEmail className="h-5 w-5 text-black" />
              </div>
              <div>
                <h2 className="font-medium dark:text-white">Email notifications</h2>
                <p className="text-sm text-gray-500">Receive notifications through your email</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.emailNotifications}
                onChange={() => handleToggle("emailNotifications")}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border dark:border-border-dark after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-400"></div>
            </label>
          </div>

          {/* SMS notifications */}
          <div className="border dark:border-border-dark border-lightGray2 rounded-lg p-6 flex items-center justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-yellow-100/50 w-8 h-8 rounded-full flex items-center justify-center">
                <BiMessageDots className="h-5 w-5 text-black" />
              </div>
              <div>
                <h2 className="font-medium dark:text-white">SMS notifications</h2>
                <p className="text-sm text-gray-500">Receive notifications through SMS</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.smsNotifications}
                onChange={() => handleToggle("smsNotifications")}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border dark:border-border-dark after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-400"></div>
            </label>
          </div>

          {/* OTP */}
          <div className="border dark:border-border-dark border-lightGray2 rounded-lg p-6 flex items-center justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-yellow-100/50 w-8 h-8 rounded-full flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 11.5V15.5C22 19 20 20.5 17 20.5H7C4 20.5 2 19 2 15.5V8.5C2 5 4 3.5 7 3.5H12" stroke="#292D32" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M7 9L10.13 11.5C11.16 12.32 12.85 12.32 13.88 11.5" stroke="#292D32" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M19.4773 2.8175L19.7573 3.38749C19.8973 3.66749 20.2473 3.9275 20.5573 3.9875L20.9373 4.0475C22.0773 4.2375 22.3473 5.0775 21.5273 5.9075L21.1773 6.25749C20.9473 6.49749 20.8173 6.95749 20.8873 7.27749L20.9373 7.4875C21.2473 8.8675 20.5173 9.39749 19.3173 8.67749L19.0573 8.52749C18.7473 8.34749 18.2473 8.34749 17.9373 8.52749L17.6773 8.67749C16.4673 9.40749 15.7373 8.8675 16.0573 7.4875L16.1073 7.27749C16.1773 6.95749 16.0473 6.49749 15.8173 6.25749L15.4673 5.9075C14.6473 5.0775 14.9173 4.2375 16.0573 4.0475L16.4373 3.9875C16.7373 3.9375 17.0973 3.66749 17.2373 3.38749L17.5173 2.8175C18.0573 1.7275 18.9373 1.7275 19.4773 2.8175Z" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>

              </div>
              <div>
                <h2 className="font-medium dark:text-white">One-Time Password (OTP)</h2>
                <p className="text-sm text-gray-500">Add a second security layer to secure your account</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.otp}
                onChange={() => handleToggle("otp")}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border dark:border-border-dark after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-400"></div>
            </label>
          </div>

          {!settings.otp && <p className="mt-2 text-sm text-red-600">
            Your account may be prone to security risks. Toggle on to avoid such circumstances
          </p>}


          <button
            className="py-3 px-5 bg-red-600 hover:bg-red-800 text-white text-sm rounded-lg"
            onClick={() => setConfirmModal(true)}
          >
            Deactivate & Delete Account Permanently
          </button>
        </div>
      </div>
    </>
  )
}

const PersonalInformation = () => {
  const [loading, setLoading] = useState(false)
  const [uploadloading, setUpLoading] = useState(false)
  const { loadUserFromCookies } = useAuth()
  const [profileImage, setProfileImage] = useState()
  const { user } = useAuth()
  const initializedUserIdRef = useRef(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    setValue,
    watch,
    reset
  } = useForm({
    defaultValues: user || {}
  })

  const region = watch("region")
  const district = watch("district")
  const { regionNames, districts, communities } = useLocationCascade(region, district)


  const onSubmit = async (formData) => {
    setLoading(true)
    try {
      const payload = JSON.stringify({
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        phone: formData.phone,
        region: formData.region,
        district: formData.district,
        community: formData.community,
        longitude: formData.longitude,
        latitude: formData.latitude,
        profile_img: formData.profile_img,
        business_name: user?.is_business_account ? formData.business_name : null
      })

      const response = await api.patch("users/me/", payload)

      if (response?.data?.id) {
        SuccessNotification("You have updated your profile successfully")
        const updatedUser = {
          ...user,
          firstname: response.data.firstname,
          lastname: response.data.lastname,
          email: response.data.email,
          phone: response.data.phone,
          region: response.data.region,
          district: response.data.district,
          community: response.data.community,
          longitude: response.data.longitude,
          latitude: response.data.latitude,
          profile_img: response.data.profile_img,
        }
        localStorage.setItem("user", JSON.stringify(updatedUser))
        reset(updatedUser)
        setLoading(false)
      }
    } catch (error) {
      ErrorNotification("An error occurred when updating profile")
      setLoading(false)
    }
  }

  const updateImage = async (e) => {
    setUpLoading(true)
    const fileUploaded = e.target.files[0]
    const size = fileUploaded?.size / 1024 / 1024

    if (size > 1) {
      alert("File size must not exceed 1MB")
      setUpLoading(false)
      return
    }

    // Show preview immediately
    setProfileImage(URL.createObjectURL(fileUploaded))

    try {
      const formData = new FormData()
      formData.append("profile_img", fileUploaded)
      // PATCH to users/me/ endpoint
      const response = await api.patch("users/me/", formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setUpLoading(false)
      const updatedUser = {
        ...user,
        profile_img: response.data.profile_img,
      }
      window.localStorage.setItem("user", JSON.stringify(updatedUser))
      reset(updatedUser)
      loadUserFromCookies()
      setProfileImage(response.data.profile_img)
    } catch (error) {
      setUpLoading(false)
      ErrorNotification("There was an error in uploading profile pic")
    }
  }

  useEffect(() => {
    if (!user?.id || initializedUserIdRef.current === user.id) return

    const hydrateProfile = async () => {
      setProfileImage(user.profile_img)
      reset(user)
      initializedUserIdRef.current = user.id
    }

    hydrateProfile()
  }, [user?.id, reset])

  return (
    <div className="space-y-4">
      <div className="border-b pb-3 border-gray-200  dark:border-border-dark w-full">
        <h1 className="text-base font-bold dark:text-white">
          Personal information
        </h1>
        <p className="text-sm text-gray-500 mt-2">
          Update your photo and personal details here.
        </p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-5xl">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="md:w-1/2">
            <label htmlFor="profile_img" className="text-sm font-semibold dark:text-white">Your photo</label>
            <p className="text-xs text-gray-500">
              This will be displayed on your profile.
            </p>
          </div>


          <div className="flex flex-row gap-7 w-full">
            <img
              src={profileImage || "https://e7.pngegg.com/pngimages/84/165/png-clipart-united-states-avatar-organization-information-user-avatar-service-computer-wallpaper-thumbnail.png"}
              alt="profile"
              className="w-14 h-14 overflow-hidden rounded-full border dark:border-border-dark border-gray-100"
            />

            <label htmlFor="profile_img" className="w-full">
              <div className="w-full h-32 border dark:border-border-dark border-gray-400 rounded-md flex flex-col items-center justify-center p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-secondaryDark">
                <svg width="46" height="46" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="40" height="40" rx="20" fill="#FEECB8" />
                  <rect x="3" y="3" width="40" height="40" rx="20" stroke="#FFF9E8" stroke-width="6" />
                  <path d="M19.666 26.3333L22.9993 23M22.9993 23L26.3327 26.3333M22.9993 23V30.5M29.666 26.9524C30.6839 26.1117 31.3327 24.8399 31.3327 23.4167C31.3327 20.8854 29.2807 18.8333 26.7493 18.8333C26.5673 18.8333 26.3969 18.7383 26.3044 18.5814C25.2177 16.7374 23.2114 15.5 20.916 15.5C17.4642 15.5 14.666 18.2982 14.666 21.75C14.666 23.4718 15.3622 25.0309 16.4885 26.1613" stroke="#FFB733" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <p className="text-sm text-gray-500 mb-2">
                  <span className="font-semibold text-yellowcolor">Click to upload</span>&nbsp;or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  SVG, PNG or JPG
                </p>
              </div>
              <input
                type="file"
                id="profile_img"
                className="hidden"
                accept="image/*"
                onChange={updateImage}
              />
            </label>
          </div>
        </div>

        <hr className="border-gray-200 dark:border-border-dark " />

        {user?.is_business_account ? <div className="flex flex-col md:flex-row gap-4">
          <label htmlFor="business_name" className="text-sm font-semibold md:w-1/2 dark:text-white">
            Business name</label>
          <input type="text" id="business_name"
            className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark  dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
            // disabled
            {...register("business_name")}
          />
          {errors.business_name && <p className="text-red-500 text-xs">{errors.business_name.message}</p>}
        </div> : <>
          <div className="flex flex-col md:flex-row gap-4">
            <label htmlFor="firstname" className="text-sm font-semibold md:w-1/2 dark:text-white">First name</label>
            <input type="text" id="firstname"
              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark  dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
              {...register("firstname")}
            />
            {errors.firstname && <p className="text-red-500 text-xs">{errors.firstname.message}</p>}
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <label htmlFor="lastname" className="text-sm font-semibold md:w-1/2 dark:text-white">Last name</label>
            <input type="text" id="lastname"
              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark  dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
              {...register("lastname")}
            />
            {errors.lastname && <p className="text-red-500 text-xs">{errors.lastname.message}</p>}
          </div>
        </>}

        <hr className="border-gray-200 dark:border-border-dark " />

        <div className="flex flex-col md:flex-row gap-4">
          <label htmlFor="email" className="text-sm font-semibold md:w-1/2 dark:text-white">Email address</label>
          <input type="email" id="email"
            className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark  dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
            // disabled

            {...register("email")}
          />
          {errors.email && <p className="text-red-500 text-xs">{errors.email.message}</p>}
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <label htmlFor="phone" className="text-sm font-semibold md:w-1/2 dark:text-white">Phone number</label>
          <input type="text" id="phone"
            className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark  dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
            {...register("phone")}
          />
          {errors.phone && <p className="text-red-500 text-xs">{errors.phone.message}</p>}
        </div>

        <hr className="border-gray-200 dark:border-border-dark " />

        <div className="flex flex-col md:flex-row gap-4">
          <label htmlFor="region" className="text-sm font-semibold md:w-1/2 dark:text-white">Region</label>
          <select id="region"
            className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark  dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
            onChange={(e) => {
              setValue("region", e.target.value)
              setValue("district", "")
              setValue("community", "")
            }}
            value={region}
          >
            <option value="">Select region</option>
            {regionNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          {errors.region && <p className="text-red-500 text-xs">{errors.region.message}</p>}
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <label htmlFor="district" className="text-sm font-semibold md:w-1/2 dark:text-white">District</label>
          <select id="district"
            className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark  dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
            onChange={(e) => {
              setValue("district", e.target.value)
              setValue("community", "")
            }}
            value={district}
          >
            <option value="">Select district</option>
            {districts?.map((d) => (
              <option key={d.name} value={d.name}>
                {d.name}
              </option>
            ))}
          </select>
          {errors.district && <p className="text-red-500 text-xs">{errors.district.message}</p>}
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <label htmlFor="community" className="text-sm font-semibold md:w-1/2 dark:text-white">Community</label>
          <select id="community"
            className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark  dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
            {...register("community")}
          >
            <option value="">Select community</option>
            {communities?.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {errors.community && <p className="text-red-500 text-xs">{errors.community.message}</p>}
        </div>

        <hr className="border-gray-200 dark:border-border-dark " />

        <div className="flex flex-col md:flex-row gap-4">
          <label htmlFor="longitude" className="text-sm font-semibold md:w-1/2 dark:text-white">Latitude (Optional)</label>
          <input type="text" id="longitude"
            className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark  dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
            {...register("longitude", { required: false })}
          />
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <label htmlFor="latitude" className="text-sm font-semibold md:w-1/2 dark:text-white">Latitude  (Optional)</label>
          <input type="text" id="latitude"
            className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark  dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
            {...register("latitude", { required: false })}
          />
        </div>

        <hr className="border-gray-200 dark:border-border-dark " />

        <div className="flex flex-row items-end justify-end gap-4 mb-10">
          <button
            type="button"
            onClick={() => {
              reset(user)
            }}
            className='px-6 text-sm py-2 border dark:border-border-dark border-black text-black rounded-lg dark:text-white'>
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className={`px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg ${loading ? "opacity-50 cursor-not-allowed" : ""}`}>
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  )
}

const ChangePassword = () => {
  const { logout } = useAuth()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset
  } = useForm()
  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false)

  const new_password = watch("new_password")
  const confirm_password = watch("confirm_password")

  const onSubmit = async (formData) => {
    try {
      const payload = JSON.stringify({
        ...formData
      })

      const { data } = await api.post("auth/passwordreset/", payload)
      if (data?.message) {
        SuccessNotification(data?.message || "Password updated successfully")
        reset()
        logout()
      } else {
        ErrorNotification(data?.error || "Failed to update password")
      }
    } catch (error) {
      ErrorNotification(error?.response?.data?.error || "Failed to update password")
    }
  }

  return (
    <div className="space-y-4">
      <div className="border-b pb-3 border-gray-200  dark:border-border-dark w-full">
        <h1 className="text-base font-bold dark:text-white">
          Password
        </h1>
        <p className="text-sm text-gray-500 mt-2">
          Kindly provide your current and new password
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-5xl">
        <div className="flex flex-col md:flex-row gap-4">
          <label htmlFor="current_password" className="text-sm font-semibold md:w-1/2 dark:text-white">Current password</label>
          <div className="w-full relative">
            <input type={showOldPassword ? "text" : "password"} id="current_password"
              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark  dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
              {...register("current_password")}
            />
            <button type="button" onClick={() => setShowOldPassword(!showOldPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
              {showOldPassword ? <FaRegEyeSlash className="w-4 h-4 dark:text-white" /> : <RiEyeLine className="w-4 h-4 dark:text-white" />}
            </button>
          </div>
          {errors.current_password && <p className="text-red-500 text-xs">{errors.current_password.message}</p>}
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <label htmlFor="new_password" className="text-sm font-semibold md:w-1/2 dark:text-white">New password</label>
          <div className="w-full relative">
            <input
              type={showNewPassword ? "text" : "password"} id="new_password"
              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark  dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
              {...register("new_password")}
            />
            <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
              {showNewPassword ? <FaRegEyeSlash className="w-4 h-4 dark:text-white" /> : <RiEyeLine className="w-4 h-4 dark:text-white" />}
            </button>
          </div>
          {errors.new_password && <p className="text-red-500 text-xs">{errors.new_password.message}</p>}
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <label htmlFor="confirm_password" className="text-sm font-semibold md:w-1/2 dark:text-white">Confirm new password</label>
          <div className="w-full relative">
            <input type={showConfirmNewPassword ? "text" : "password"} id="confirm_password"
              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark  dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
              {...register("confirm_password", {
                validate: (value) => {
                  if (value !== new_password) {
                    return "Passwords do not match"
                  }
                }
              })}
            />
            <button type="button" onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
              {showConfirmNewPassword ? <FaRegEyeSlash className="w-4 h-4 dark:text-white" /> : <RiEyeLine className="w-4 h-4 dark:text-white" />}
            </button>
          </div>
          {errors.confirm_password && <p className="text-red-500 text-xs">{errors.confirm_password.message}</p>}
        </div>

        <div className="flex flex-row items-end justify-end gap-4 mb-10">
          {/* <button
            type="button"
            onClick={() => {
              reset()
            }}
            className='px-6 text-sm py-2 border dark:text-white dark:border-border-dark border-black text-black rounded-lg'>
            Cancel
          </button> */}

          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}>
            {isSubmitting ? "Saving..." : "Update Password"}
          </button>
        </div>
      </form>
    </div>
  )
}

const SenderIdManagement = () => {
  // Fetch SMS sender IDs
  const { data: smsSenderIdsData, isLoading: loadingSenderIds, mutate: mutateSenderIds } = useSWR(`sms-sender-ids/`, fetcher)

  const smsSenderIds = Array.isArray(smsSenderIdsData) ? smsSenderIdsData : []
  const primarySenderId = smsSenderIds[0] || null
  const accountDefaultId = primarySenderId?.default_sender_id || null
  const accountCustomSenderId = getAccountCustomSenderId(smsSenderIds)
  const approvedCustomIds = smsSenderIds.filter(isApprovedCustomSenderId)
  const pendingCustomIds = smsSenderIds.filter(isPendingCustomRequest)

  const [togglingSenderId, setTogglingSenderId] = useState(null)

  // Handle toggle sender ID enable/disable
  const handleToggleSenderId = async (senderId) => {
    if (togglingSenderId) return // Prevent multiple simultaneous toggles

    setTogglingSenderId(senderId.id)
    try {
      const newIsEnabled = !senderId.is_enabled
      await api.patch(`sms-sender-ids/${senderId.id}/`, {
        is_enabled: newIsEnabled ? 1 : 0
      })

      // Update local state
      mutateSenderIds()

      SuccessNotification(`SMS Nickname ${newIsEnabled ? 'enabled' : 'disabled'} successfully`)
    } catch (err) {
      ErrorNotification(err?.response?.data?.message || `Failed to ${senderId.is_enabled ? 'disable' : 'enable'} SMS Nickname`)
    } finally {
      setTogglingSenderId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="border-b pb-3 border-gray-200 dark:border-border-dark w-full">
        <h1 className="text-base font-bold dark:text-white">
          SMS Nickname
        </h1>
        <p className="text-sm text-gray-500 mt-2">
          Manage your SMS Nickname for text campaigns. Campaigns use the platform default unless you request and enable a custom Nickname.
        </p>
      </div>

      {loadingSenderIds ? (
        <div className="flex items-center justify-center py-10">
          <Spinner />
          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading SMS Nicknames...</span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Defaults in use — not the pending custom request */}
          <div className="border dark:border-border-dark border-gray-200 rounded-lg p-4 bg-gray-50 dark:bg-dark space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">
                  Platform Default
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Fallback SMS Nickname when no custom Nickname is approved on your account.
                </p>
                <span className="font-semibold uppercase text-base dark:text-white">
                  {PLATFORM_DEFAULT_SENDER_ID}
                </span>
              </div>
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded-full text-xs font-medium shrink-0">
                Platform
              </span>
            </div>
            <div className="border-t dark:border-border-dark border-gray-200 pt-4">
              <div className="flex flex-row justify-between mb-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Your account custom SMS Nickname
                </p>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded-full text-xs font-medium shrink-0">
                  Custom
                </span>
              </div>
              {accountCustomSenderId && approvedCustomIds.length > 0 ? (
                approvedCustomIds.length > 0 && approvedCustomIds?.map((senderId) => (

                  <div key={senderId.id} className=" flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-semibold text-base dark:text-white">
                          {getApprovedCustomSenderId(senderId)}
                        </span>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded-full text-xs font-medium">
                          Approved
                        </span>
                        {senderId.is_enabled && (
                          <span className="px-2 py-0.5 bg-yellowcolor/20 text-yellow-800 dark:text-yellow-400 rounded-full text-xs font-medium">
                            Enabled
                          </span>
                        )}
                      </div>
                      {!senderId.is_enabled && getApprovedCustomSenderId(senderId) !== getActiveSenderId(senderId) && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Campaigns currently use{' '}
                          {/* <span className="font-medium uppercase">{getActiveSenderId(senderId)}</span> while this ID is disabled. */}
                          <span className="font-medium uppercase">HARVSTALERT</span> while this ID is disabled
                        </div>
                      )}
                    </div>

                    {/* Toggle Enable/Disable */}
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {senderId.is_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={senderId.is_enabled}
                          onChange={() => handleToggleSenderId(senderId)}
                          disabled={togglingSenderId === senderId.id}
                          className="sr-only peer"
                        />
                        <div className={`w-11 h-6 rounded-full transition-colors duration-200 ${senderId.is_enabled
                          ? 'bg-yellowcolor'
                          : 'bg-gray-200 dark:bg-gray-700'
                          } peer-focus:ring-2 peer-focus:ring-yellowcolor relative ${togglingSenderId === senderId.id ? 'opacity-50 cursor-not-allowed' : ''
                          }`}>
                          <div className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${senderId.is_enabled ? 'translate-x-5' : ''
                            }`}></div>
                        </div>
                      </label>
                    </div>
                  </div>
                ))
                // <div className="pt-3 border-t dark:border-border-dark border-gray-200">
                //   <div className="flex items-start justify-between gap-4">
                //     <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                //       Your account custom sender ID
                //     </p>
                //     <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded-full text-xs font-medium shrink-0">
                //       Custom
                //     </span>
                //     {primarySenderId && accountCustomSenderId !== getActiveSenderId(primarySenderId) && (
                //       <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                //         Campaigns currently use{' '}
                //         {/* <span className="font-medium uppercase">{getActiveSenderId(primarySenderId)}</span>. */}
                //         <span className="font-medium uppercase">SOLIDARIDAD</span>
                //       </p>
                //     )}
                //   </div>
                //   <span className="font-semibold uppercase text-base dark:text-white">
                //     {accountCustomSenderId}
                //   </span>

                // </div>
              ) : accountDefaultId ? (
              <></>
              ) : null}
            </div>
          </div>


          <RequestSenderIdForm
            onSuccess={() => mutateSenderIds()}
            existingSenderId={primarySenderId?.id}
            currentSenderId={accountCustomSenderId || accountDefaultId}
          />

          {/* Approved custom sender IDs */}
      

          {/* Custom requests awaiting approval — show sender_id (what user submitted) */}
          {pendingCustomIds.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                Custom Request — Pending Approval
              </h2>
              <div className="space-y-3">
                {pendingCustomIds.map((senderId) => (
                  <div
                    key={senderId.id}
                    className="border dark:border-border-dark border-gray-200 rounded-lg p-4 bg-white dark:bg-secondaryDark"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold text-base dark:text-white">
                            {getSubmittedSenderId(senderId)}
                          </span>
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300 rounded-full text-xs font-medium capitalize">
                            {senderId.status || 'Pending'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Awaiting admin approval. Campaigns continue using{' '}
                          <span className="font-medium">{getActiveSenderId(senderId)}</span> until approved.
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {pendingCustomIds.length === 0 && approvedCustomIds.length === 0 && !loadingSenderIds && (
            <div className="text-center py-4">
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                No custom SMS Nickname request yet. Use the form above to submit one.
              </p>
            </div>
          )}

        </div>
      )}
    </div>
  )
}

const PrivateNumberManagement = () => (
  <div className="space-y-4">
    <div className="border-b pb-3 border-gray-200 dark:border-border-dark w-full">
      <h1 className="text-base font-bold dark:text-white">Private Number</h1>
      <p className="text-sm text-gray-500 mt-2">
        Request a dedicated caller ID for voice campaigns. Available for service provider accounts.
      </p>
    </div>
    <RequestPrivateNumberSection />
  </div>
)

export default Index
